import React, { useState, useEffect } from 'react';
import { NavLink, Link, useLocation, useNavigate } from 'react-router-dom';
import { useTenant } from '../../contexts/TenantContext';
import { useAuth } from '../../contexts/AuthContext';
import { useMessages } from '../../contexts/MessageContext';
import NotificationsPopover from '../notifications/NotificationsPopover';
import financialRequestService from '../../services/financialRequestService';

import {
  Box, Drawer, AppBar, Toolbar, List, Typography, Divider,
  IconButton, ListItem, ListItemButton, ListItemIcon, ListItemText,
  Avatar, Menu, MenuItem, Tooltip, Badge, Chip, useTheme, useMediaQuery,
  Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle, Button,
  Paper, CircularProgress, alpha, Alert, Slide, Zoom, styled, Backdrop
} from '@mui/material';

import {
  Dashboard as DashboardIcon,
  Person as PersonIcon,
  ExitToApp as LogoutIcon,
  Menu as MenuIcon,
  Help as HelpIcon,
  People as PeopleIcon,
  AccountCircle as AccountCircleIcon,
  Notifications as NotificationsIcon,
  AccountBalanceWallet as WalletIcon,
  MonetizationOn as MonetizationOnIcon,
  Receipt as ReceiptIcon,
  CurrencyExchange as CurrencyExchangeIcon,
  Storefront as StorefrontIcon,
  Refresh as RefreshIcon,
  Email as EmailIcon,
  Security as SecurityIcon,
  AccessTime as AccessTimeIcon,
  Info as InfoIcon,
  Warning as WarningIcon,
  CheckCircle as CheckCircleIcon,
  AccountTree as AccountTreeIcon,
  Timeline as TimelineIcon,
} from '@mui/icons-material';
import { format } from 'date-fns';

// Transition component for logout dialog
const SlideTransition = React.forwardRef(function Transition(props, ref) {
  return <Slide direction="up" ref={ref} {...props} />;
});

// Styled component for the security icon
const StyledSecurityIcon = styled(SecurityIcon)(({ theme }) => ({
  fontSize: 45,
  color: alpha(theme.palette.primary.main, 0.9),
  marginRight: theme.spacing(2),
}));

const drawerWidth = 240;

const MemberSidebar = ({ children }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const { tenant } = useTenant();
  const { user, logout } = useAuth();
  const { unreadCount } = useMessages();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [mobileOpen, setMobileOpen] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);
  const [logoutDialogOpen, setLogoutDialogOpen] = useState(false);
  const [financialRequestAlerts, setFinancialRequestAlerts] = useState({
    approved: 0,
    disbursed: 0,
    declined: 0
  });
  
  // New state for logout process
  const [isLoggingOut, setIsLoggingOut] = useState(false);
  const [logoutSuccess, setLogoutSuccess] = useState(false);
  const [loginTime, setLoginTime] = useState(null);

  // Set login time on component mount
  useEffect(() => {
    const storedLoginTime = localStorage.getItem('loginTime');
    if (storedLoginTime) {
      setLoginTime(new Date(parseInt(storedLoginTime)));
    } else {
      // If no login time is stored, set to now and store it
      const now = new Date();
      setLoginTime(now);
      localStorage.setItem('loginTime', now.getTime().toString());
    }
  }, []);

  // Safe function to get initials
  const getInitials = (firstName, lastName) => {
    const firstInitial = (firstName && typeof firstName === 'string' && firstName.length > 0) ? firstName.charAt(0) : '';
    const lastInitial = (lastName && typeof lastName === 'string' && lastName.length > 0) ? lastName.charAt(0) : '';
    return (firstInitial + lastInitial) || '?';
  };

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const handleMenuClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const openLogoutDialog = () => {
    handleMenuClose();
    setLogoutDialogOpen(true);
  };

  const handleLogout = async () => {
    try {
      setIsLoggingOut(true);
      await new Promise(resolve => setTimeout(resolve, 1000)); // Brief delay for visual feedback
      await logout();
      
      // Show success feedback briefly before redirecting
      setLogoutSuccess(true);
      localStorage.removeItem('loginTime');
      
      // Close the dialog and drawer after a short delay
      setTimeout(() => {
        setLogoutDialogOpen(false);
        setMobileOpen(false);
        navigate('/login');
      }, 1500);
    } catch (error) {
      console.error('Logout failed:', error);
      // Even if there's an error, attempt to clear localStorage and redirect
      localStorage.clear();
      navigate('/login');
    }
  };

  // Fetch financial request updates that need member attention
  useEffect(() => {
    const fetchFinancialRequestAlerts = async () => {
      try {
        // Get member's financial requests
        const response = await financialRequestService.getMemberRequests();
        if (response && response.data) {
          // Count requests by status
          const counts = {
            approved: 0,
            disbursed: 0,
            declined: 0
          };
          
          response.data.forEach(request => {
            if (request.status === 'Approved') {
              counts.approved++;
            } else if (request.status === 'Disbursed') {
              counts.disbursed++;
            } else if (request.status === 'Declined') {
              counts.declined++;
            }
          });
          
          setFinancialRequestAlerts(counts);
        }
      } catch (error) {
        console.error('Error fetching financial request alerts:', error);
      }
    };

    if (user) {
      fetchFinancialRequestAlerts();
      // Refresh alerts every 5 minutes
      const intervalId = setInterval(fetchFinancialRequestAlerts, 5 * 60 * 1000);
      return () => clearInterval(intervalId);
    }
  }, [user]);

  // Check if directory is enabled
  const isDirectoryEnabled = tenant?.settings?.directory?.enabled === true;

  // Calculate total action-needed financial requests
  const totalFinancialAlerts = financialRequestAlerts.approved + financialRequestAlerts.disbursed;
  // Calculate declined requests
  const declinedRequests = financialRequestAlerts.declined;

  const menuItems = [
    {
      text: 'Dashboard',
      icon: <DashboardIcon />,
      path: '/member/dashboard',
    },
    {
      text: 'My Profile',
      icon: <PersonIcon />,
      path: '/member/profile',
    },
    {
      text: 'Messages',
      icon: unreadCount > 0 ? (
        <Badge badgeContent={unreadCount} color="error" sx={{ '& .MuiBadge-badge': { right: -3, top: 3 } }}>
          <EmailIcon />
        </Badge>
      ) : <EmailIcon />,
      path: '/member/messages',
    },
    {
      text: 'Wallet',
      icon: <WalletIcon />,
      path: '/member/wallet',
    },
  
    {
      text: 'Workflows',
      icon: <TimelineIcon />,
      path: '/member/workflows',
    },
    {
      text: 'Financial Request',
      icon: (
        <>
          {totalFinancialAlerts > 0 && (
            <Badge 
              badgeContent={totalFinancialAlerts} 
              color="success" 
              sx={{ '& .MuiBadge-badge': { right: -3, top: 3 } }}
            >
              <MonetizationOnIcon />
            </Badge>
          )}
          {totalFinancialAlerts === 0 && declinedRequests > 0 && (
            <Badge 
              badgeContent={declinedRequests} 
              color="error" 
              sx={{ '& .MuiBadge-badge': { right: -3, top: 3 } }}
            >
              <MonetizationOnIcon />
            </Badge>
          )}
          {totalFinancialAlerts === 0 && declinedRequests === 0 && (
            <MonetizationOnIcon />
          )}
        </>
      ),
      path: '/member/financial-requests',
    },
    {
      text: 'Member Directory',
      icon: <PeopleIcon />,
      path: '/member/directory',
      visible: isDirectoryEnabled
    },
    {
      text: 'Mental Wellness',
      icon: <RefreshIcon />,
      path: '/member/reset',
    },
    {
      text: 'Help & Support',
      icon: <HelpIcon />,
      path: '/member/help',
    },
  ];

  // Filter items that should be visible
  const visibleItems = menuItems.filter(item => item.visible !== false);

  const drawer = (
    <Box>
      <Toolbar sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        py: 2
      }}>
        <Avatar
          src={user?.profilePicture || tenant?.logo || ""}
          sx={{
            width: 80,
            height: 80,
            mb: 1,
            bgcolor: theme.palette.primary.main
          }}
          alt={`${user?.firstName || 'Member'} ${user?.lastName || 'User'}`}
        >
          {getInitials(user?.firstName, user?.lastName)}
        </Avatar>
        <Typography variant="h6" sx={{ mt: 1 }}>
          {user?.firstName || 'Member'} {user?.lastName || 'User'}
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ color: 'rgba(255,255,255,0.7)' }}>
          {user?.email || 'member@example.com'}
        </Typography>
        <Chip
          label="Member"
          color="primary"
          size="small"
          sx={{ mt: 1 }}
        />
      </Toolbar>
      <Divider sx={{ borderColor: 'rgba(255,255,255,0.1)' }} />
      <List>
        {visibleItems.map((item) => {
          // Check if current path starts with this menu item's path
          const isActive = location.pathname === item.path || 
                          (item.path !== "/member/dashboard" && location.pathname.startsWith(item.path));
          
          return (
            <ListItem 
              disablePadding
              key={item.text}
            >
              <ListItemButton
                component={NavLink}
                to={item.path}
                selected={isActive}
                onClick={() => isMobile && setMobileOpen(false)} // Close drawer on mobile after click
                sx={{
                  borderLeft: isActive ? `4px solid ${theme.palette.primary.main}` : '4px solid transparent',
                  bgcolor: isActive ? 'rgba(255,255,255,0.1)' : 'transparent',
                  '&:hover': {
                    bgcolor: 'rgba(255,255,255,0.05)',
                  }
                }}
              >
                <ListItemIcon sx={{ color: isActive ? theme.palette.primary.main : 'rgba(255,255,255,0.7)' }}>
                  {item.icon}
                </ListItemIcon>
                <ListItemText 
                  primary={item.text}
                  primaryTypographyProps={{
                    sx: {
                      color: isActive ? '#fff' : 'rgba(255,255,255,0.7)',
                      fontWeight: isActive ? '500' : 'normal'
                    }
                  }}
                />
              </ListItemButton>
            </ListItem>
          );
        })}
      </List>
      <Divider sx={{ borderColor: 'rgba(255,255,255,0.1)' }} />
      <List>
        <ListItem disablePadding>
          <ListItemButton 
            onClick={openLogoutDialog}
            sx={{
              '&:hover': {
                bgcolor: 'rgba(255,255,255,0.05)',
              },
            }}
          >
            <ListItemIcon sx={{ color: 'rgba(255,255,255,0.7)' }}>
              <LogoutIcon />
            </ListItemIcon>
            <ListItemText 
              primary="Logout"
              primaryTypographyProps={{
                sx: { color: 'rgba(255,255,255,0.7)' }
              }}
            />
          </ListItemButton>
        </ListItem>
      </List>
    </Box>
  );

  return (
    <Box sx={{ display: 'flex', bgcolor: '#f5f7fa', minHeight: '100vh' }}>
      <AppBar
        position="fixed"
        sx={{
          width: '100%',
          bgcolor: tenant?.primaryColor || '#3f51b5',
          display: { md: 'none' },
          zIndex: (theme) => theme.zIndex.drawer + 1,
        }}
      >
        <Toolbar>
          <IconButton
            color="inherit"
            aria-label="open drawer"
            edge="start"
            onClick={handleDrawerToggle}
            sx={{ mr: 2 }}
          >
            <MenuIcon />
          </IconButton>
          <Typography variant="h6" noWrap component="div" sx={{ flexGrow: 1 }}>
            {tenant?.name || 'Member Portal'}
          </Typography>
          <Box sx={{ flexGrow: 1 }} />
          
          {/* Replace the notification badge with our notification popover */}
          <NotificationsPopover />
          
          <Tooltip title="Account settings">
            <IconButton
              onClick={handleMenuClick}
              color="inherit"
              size="large"
              aria-controls="user-menu"
              aria-haspopup="true"
            >
              <AccountCircleIcon />
            </IconButton>
          </Tooltip>
          <Menu
            id="user-menu"
            anchorEl={anchorEl}
            open={Boolean(anchorEl)}
            onClose={handleMenuClose}
            transformOrigin={{ horizontal: 'right', vertical: 'top' }}
            anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
          >
            <MenuItem component={Link} to="/member/profile" onClick={handleMenuClose}>
              <ListItemIcon>
                <PersonIcon fontSize="small" />
              </ListItemIcon>
              <ListItemText>My Profile</ListItemText>
            </MenuItem>
            <Divider />
            <MenuItem onClick={openLogoutDialog}>
              <ListItemIcon>
                <LogoutIcon fontSize="small" />
              </ListItemIcon>
              <ListItemText>Logout</ListItemText>
            </MenuItem>
          </Menu>
        </Toolbar>
      </AppBar>
      <Box
        component="nav"
        sx={{ width: { md: drawerWidth }, flexShrink: { md: 0 } }}
      >
        <Drawer
          variant="temporary"
          open={mobileOpen}
          onClose={handleDrawerToggle}
          ModalProps={{
            keepMounted: true, // Better open performance on mobile
          }}
          sx={{
            display: { xs: 'block', md: 'none' },
            '& .MuiDrawer-paper': { 
              boxSizing: 'border-box', 
              width: drawerWidth,
              bgcolor: '#1a2138', // Dark blue background
              color: 'white',
              marginTop: { xs: '56px', sm: '64px' }, // Adjust for app bar height
            },
          }}
        >
          {drawer}
        </Drawer>
        <Drawer
          variant="permanent"
          sx={{
            display: { xs: 'none', md: 'block' },
            '& .MuiDrawer-paper': { 
              boxSizing: 'border-box', 
              width: drawerWidth,
              bgcolor: '#1a2138', // Dark blue background
              color: 'white',
            },
          }}
          open
        >
          {drawer}
        </Drawer>
      </Box>
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          p: 0, // Remove all padding for desktop
          px: { xs: 0.5, md: 0 }, // Add horizontal padding only for mobile (xs and sm sizes)
          ml: 0, // Remove left margin
          width: { md: `calc(100% - ${drawerWidth}px)` },
          mt: { xs: 1, md: 0 },
        }}
      >
        <Toolbar sx={{ display: { md: 'none' }, minHeight: { xs: '24px' } }} />
        {children}
      </Box>

      {/* Enhanced Logout Confirmation Dialog */}
      <Dialog
        open={logoutDialogOpen}
        onClose={() => !isLoggingOut && setLogoutDialogOpen(false)}
        aria-labelledby="logout-dialog-title"
        aria-describedby="logout-dialog-description"
        TransitionComponent={SlideTransition}
        PaperProps={{
          sx: {
            borderRadius: 2,
            boxShadow: 24,
            width: { xs: '90%', sm: '450px' },
            overflow: 'hidden',
            backgroundImage: `radial-gradient(circle at top right, ${alpha(tenant?.primaryColor || theme.palette.primary.main, 0.08)}, transparent 70%)`,
          }
        }}
      >
        <DialogTitle 
          id="logout-dialog-title"
          sx={{
            display: 'flex',
            alignItems: 'center',
            pt: 3,
            pb: 2,
            background: `linear-gradient(to right, ${alpha(theme.palette.background.paper, 0.7)}, ${alpha(theme.palette.background.paper, 0.98)})`,
            borderBottom: `1px solid ${alpha(theme.palette.divider, 0.1)}`
          }}
        >
          {logoutSuccess ? (
            <Zoom in={logoutSuccess}>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <CheckCircleIcon color="success" sx={{ fontSize: 28, mr: 1.5 }} />
                <Typography variant="h6" component="div">
                  Logout Successful
                </Typography>
              </Box>
            </Zoom>
          ) : (
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <StyledSecurityIcon />
              <Box>
                <Typography variant="h6" component="div">
                  Confirm Logout
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {tenant?.name || 'Member Portal'} - {new Date().toLocaleDateString()}
                </Typography>
              </Box>
            </Box>
          )}
        </DialogTitle>
        
        <DialogContent sx={{ p: 3, pt: 3 }}>
          {logoutSuccess ? (
            <Alert 
              severity="success" 
              variant="filled"
              sx={{ 
                boxShadow: '0 4px 6px rgba(0,0,0,0.1)',
                borderRadius: 1 
              }}
            >
              You have been successfully logged out. Redirecting to login page...
            </Alert>
          ) : (
            <>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <Avatar
                  src={user?.profilePicture || ""}
                  sx={{ 
                    width: 50, 
                    height: 50,
                    border: `2px solid ${alpha(tenant?.primaryColor || theme.palette.primary.main, 0.3)}`,
                    bgcolor: tenant?.primaryColor || theme.palette.primary.main,
                    boxShadow: '0 4px 8px rgba(0,0,0,0.1)'
                  }}
                >
                  {getInitials(user?.firstName, user?.lastName)}
                </Avatar>
                <Box sx={{ ml: 2 }}>
                  <Typography variant="subtitle1" fontWeight="medium">
                    {user?.firstName || 'Member'} {user?.lastName || 'User'}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {user?.email || 'member@example.com'}
                  </Typography>
                </Box>
              </Box>
              
              <Paper 
                variant="outlined" 
                sx={{ 
                  p: 2, 
                  mb: 2.5, 
                  bgcolor: alpha(theme.palette.primary.light, 0.04),
                  border: `1px solid ${alpha(theme.palette.primary.main, 0.1)}`,
                  borderRadius: 1.5
                }}
              >
                <Typography variant="body1" paragraph sx={{ display: 'flex', alignItems: 'flex-start' }}>
                  <InfoIcon color="primary" sx={{ fontSize: 20, mr: 1, mt: 0.3, opacity: 0.8 }} />
                  Logging out will end your current session. You will need to sign in again to access your account.
                </Typography>
                
                {loginTime && (
                  <Box sx={{ display: 'flex', alignItems: 'center', mt: 1 }}>
                    <AccessTimeIcon sx={{ color: 'text.secondary', mr: 1, fontSize: 18 }} />
                    <Typography variant="body2" color="text.secondary">
                      Session started: {format(loginTime, 'MMM d, yyyy, h:mm a')}
                    </Typography>
                  </Box>
                )}
                
                <Box sx={{ display: 'flex', alignItems: 'center', mt: 1 }}>
                  <PersonIcon sx={{ color: 'text.secondary', mr: 1, fontSize: 18 }} />
                  <Typography variant="body2" color="text.secondary">
                    Logged in as Member | {tenant?.name || 'Organization'}
                  </Typography>
                </Box>
              </Paper>
              
              <Alert 
                severity="warning" 
                icon={<WarningIcon />}
                variant="outlined"
                sx={{ 
                  borderRadius: 1.5,
                  fontSize: '0.875rem'
                }}
              >
                Any unsaved changes may be lost when you log out.
              </Alert>
            </>
          )}
        </DialogContent>
        
        {!logoutSuccess && (
          <DialogActions sx={{ px: 3, py: 2.5, borderTop: `1px solid ${alpha(theme.palette.divider, 0.1)}` }}>
            <Button 
              onClick={() => setLogoutDialogOpen(false)} 
              color="inherit"
              disabled={isLoggingOut}
              sx={{ 
                px: 3, 
                borderRadius: 1.5,
                '&:hover': { bgcolor: alpha(theme.palette.text.primary, 0.04) }
              }}
            >
              Cancel
            </Button>
            <Button 
              onClick={handleLogout} 
              color="primary" 
              variant="contained" 
              disabled={isLoggingOut}
              sx={{ 
                px: 3,
                borderRadius: 1.5,
                bgcolor: tenant?.primaryColor || 'primary.main',
                '&:hover': { bgcolor: tenant?.primaryColor ? alpha(tenant.primaryColor, 0.8) : 'primary.dark' },
                boxShadow: 'none'
              }}
              startIcon={isLoggingOut ? <CircularProgress size={20} color="inherit" /> : <LogoutIcon />}
            >
              {isLoggingOut ? 'Logging out...' : 'Logout'}
            </Button>
          </DialogActions>
        )}
      </Dialog>
      
      {/* Backdrop for logout success */}
      <Backdrop
        sx={{ color: '#fff', zIndex: (theme) => theme.zIndex.drawer + 1 }}
        open={logoutSuccess}
      >
        <CircularProgress color="inherit" />
      </Backdrop>
    </Box>
  );
};

export default MemberSidebar;